import React, { useState } from "react"
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CssBaseline,
  useTheme,
  useMediaQuery,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material"
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  LocalShipping as ShippingIcon,
  Inventory as InventoryIcon,
  TrackChanges as TrackingIcon,
  Analytics as AnalyticsIcon,
  AccountCircle,
  Logout,
  Notifications,
} from "@mui/icons-material"
import { useAuth } from "../contexts/AuthContext"
import { useNavigate } from "react-router-dom"
import Overview from "./Overview"
import Shipments from "./Shipments"
import Inventory from "./Inventory"
import Tracking from "./Tracking"
import Analytics from "./Analytics"

const drawerWidth = 240

interface DashboardProps {}

const Dashboard: React.FC<DashboardProps> = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const [mobileOpen, setMobileOpen] = useState(false)
  const [currentView, setCurrentView] = useState("overview")
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleProfileMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    logout()
    navigate("/login")
    handleProfileMenuClose()
  }

  const menuItems = [
    { id: "overview", label: "Overview", icon: <DashboardIcon /> },
    { id: "shipments", label: "Shipments", icon: <ShippingIcon /> },
    { id: "inventory", label: "Inventory", icon: <InventoryIcon /> },
    { id: "tracking", label: "Tracking", icon: <TrackingIcon /> },
    { id: "analytics", label: "Analytics", icon: <AnalyticsIcon /> },
  ]

  const drawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Toolbar
        sx={{
          background: "linear-gradient(135deg, #1a237e 0%, #534bae 100%)",
          color: "white",
          minHeight: "80px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant="h5"
            noWrap
            component="div"
            sx={{
              fontWeight: 700,
              background: "linear-gradient(45deg, #ffffff 30%, #e3f2fd 90%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 0.5,
            }}
          >
            🚛 Logistics
          </Typography>
          <Typography
            variant="body2"
            sx={{
              opacity: 0.9,
              fontSize: "0.75rem",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
            }}
          >
            Platform
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <List sx={{ flex: 1, px: 1, py: 2 }}>
        {menuItems.map((item) => (
          <ListItemButton
            key={item.id}
            onClick={() => {
              setCurrentView(item.id)
              if (isMobile) {
                setMobileOpen(false)
              }
            }}
            selected={currentView === item.id}
            sx={{
              borderRadius: 2,
              mb: 0.5,
              mx: 0.5,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: theme.palette.primary.light + "20",
                transform: "translateX(4px)",
              },
              "&.Mui-selected": {
                background: "linear-gradient(135deg, #1a237e 0%, #534bae 100%)",
                color: "white",
                boxShadow: "0px 4px 12px rgba(26, 35, 126, 0.3)",
                "& .MuiListItemIcon-root": {
                  color: "white",
                },
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #000051 0%, #1a237e 100%)",
                  transform: "translateX(4px)",
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontWeight: currentView === item.id ? 600 : 500,
                fontSize: "0.9rem",
              }}
            />
          </ListItemButton>
        ))}
      </List>
    </Box>
  )

  const renderContent = () => {
    switch (currentView) {
      case "overview":
        return <Overview />
      case "shipments":
        return <Shipments />
      case "inventory":
        return <Inventory />
      case "tracking":
        return <Tracking />
      case "analytics":
        return <Analytics />
      default:
        return <Overview />
    }
  }

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
          boxShadow:
            "0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)",
        }}
      >
        <Toolbar
          sx={{
            minHeight: "70px",
            padding: { xs: "0 16px", sm: "0 16px", md: "0 16px" },
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              display: { md: "none" },
              color: theme.palette.text.primary,
              "&:hover": {
                backgroundColor: theme.palette.primary.light + "20",
              },
            }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              minWidth: 0,
            }}
          >
            <Typography
              variant="h5"
              noWrap
              component="div"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                background: "linear-gradient(135deg, #1a237e 0%, #534bae 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {menuItems.find((item) => item.id === currentView)?.label ||
                "Dashboard"}
            </Typography>
            <Box
              sx={{
                ml: 2,
                height: 24,
                width: 1,
                background: "linear-gradient(90deg, #1a237e 0%, #534bae 100%)",
                borderRadius: 1,
                opacity: 0.3,
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            <IconButton
              sx={{
                color: theme.palette.text.primary,
                "&:hover": {
                  backgroundColor: theme.palette.primary.light + "20",
                },
              }}
            >
              <Badge
                badgeContent={4}
                color="error"
                sx={{ "& .MuiBadge-badge": { fontSize: "0.7rem" } }}
              >
                <Notifications />
              </Badge>
            </IconButton>
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls="primary-search-account-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              sx={{
                "&:hover": {
                  backgroundColor: theme.palette.primary.light + "20",
                },
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  background:
                    "linear-gradient(135deg, #1a237e 0%, #534bae 100%)",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                }}
              >
                {user?.name?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
          </Box>
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
          >
            <MenuItem onClick={handleProfileMenuClose}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 4 },
          pt: { xs: 4, md: 6 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
          minHeight: "100vh",
        }}
      >
        <Toolbar />
        <Box
          sx={{
            maxWidth: "1400px",
            mx: "auto",
            mt: 2,
            "& > *": {
              animation: "fadeInUp 0.6s ease-out",
            },
          }}
        >
          {renderContent()}
        </Box>
      </Box>
    </Box>
  )
}

export default Dashboard
